import React, { useMemo, useState } from 'react';
import TagInput from './TagInput';
import './TagGroupInput.css';
import { normalizeTagValue } from '../utils/tagUtils';
import { buildTagGroups, buildTagGroupsFromRaw } from '../utils/tagGroupUtils';

const noop = () => {};

const TAG_CATEGORIES = Object.freeze([
    { id: 'subjects', label: 'Subjects' },
    { id: 'geography', label: 'Geography' },
    { id: 'persons', label: 'Persons' },
]);

const normalizeTags = (rawTags) => {
    const source = Array.isArray(rawTags)
        ? rawTags
        : (typeof rawTags === 'string'
            ? rawTags.split(',')
            : []);

    const unique = [];
    const seen = new Set();

    source.forEach((tag) => {
        const normalized = normalizeTagValue(tag);
        if (!normalized || seen.has(normalized)) {
            return;
        }
        seen.add(normalized);
        unique.push(normalized);
    });

    return unique;
};

const toAssignmentMap = (value) => {
    const map = new Map();

    (Array.isArray(value) ? value : []).forEach((entry) => {
        const groupId = typeof entry?.groupId === 'string'
            ? entry.groupId.trim()
            : (typeof entry?.id === 'string' ? entry.id.trim() : '');

        if (!groupId) {
            return;
        }

        const tags = normalizeTags(entry?.tags ?? entry?.subTags ?? []);
        map.set(groupId, tags);
    });

    return map;
};

const TagGroupInput = ({
    groups = [],
    value = [],
    onChange = noop,
    disabled = false,
    required = false,
}) => {
    const [activeCategory, setActiveCategory] = useState('subjects');

    const classifiedRawGroups = useMemo(() => {
        const source = Array.isArray(groups) ? groups : [];
        const buckets = {
            subjects: [],
            geography: [],
            persons: [],
        };

        source.forEach((group) => {
            if (!group) {
                return;
            }

            const category = typeof group.category === 'string' ? group.category.trim().toLowerCase() : '';
            if (category === 'geography') {
                buckets.geography.push(group);
                return;
            }
            if (category === 'persons') {
                buckets.persons.push(group);
                return;
            }
            buckets.subjects.push(group);
        });

        return buckets;
    }, [groups]);

    const subjectGroups = useMemo(() => buildTagGroups(classifiedRawGroups.subjects), [classifiedRawGroups.subjects]);
    const geographyGroups = useMemo(() => buildTagGroupsFromRaw(classifiedRawGroups.geography), [classifiedRawGroups.geography]);
    const personGroups = useMemo(() => buildTagGroupsFromRaw(classifiedRawGroups.persons), [classifiedRawGroups.persons]);

    const allDisplayGroups = useMemo(() => {
        const groupMap = new Map();
        [...subjectGroups, ...geographyGroups, ...personGroups].forEach((group) => {
            if (group?.id && !groupMap.has(group.id)) {
                groupMap.set(group.id, group);
            }
        });
        return [...groupMap.values()];
    }, [subjectGroups, geographyGroups, personGroups]);

    const activeGroups = useMemo(() => {
        if (activeCategory === 'geography') {
            return geographyGroups;
        }
        if (activeCategory === 'persons') {
            return personGroups;
        }
        return subjectGroups;
    }, [activeCategory, geographyGroups, personGroups, subjectGroups]);
    const assignmentMap = useMemo(() => toAssignmentMap(value), [value]);

    const handleGroupChange = (groupId, nextTags) => {
        if (disabled) {
            return;
        }

        const normalizedNext = normalizeTags(nextTags);
        const nextMap = new Map(assignmentMap);

        // Enforce global uniqueness by "moving" tags into the most recently edited group.
        const normalizedNextSet = new Set(normalizedNext);
        nextMap.forEach((tags, existingGroupId) => {
            if (existingGroupId === groupId) {
                return;
            }
            const filtered = (Array.isArray(tags) ? tags : []).filter((tag) => !normalizedNextSet.has(normalizeTagValue(tag)));
            if (filtered.length > 0) {
                nextMap.set(existingGroupId, filtered);
            } else {
                nextMap.delete(existingGroupId);
            }
        });

        if (normalizedNext.length > 0) {
            nextMap.set(groupId, normalizedNext);
        } else {
            nextMap.delete(groupId);
        }

        const nextValue = allDisplayGroups
            .map((group) => ({
                groupId: group.id,
                tags: nextMap.get(group.id) || [],
            }))
            .filter((entry) => Array.isArray(entry.tags) && entry.tags.length > 0);

        onChange(nextValue);
    };

    return (
        <div className="tag-group-input" data-required={required ? 'true' : 'false'}>
            <div className="tag-group-input__tabs author-tabs" role="tablist" aria-label="Tag categories">
                {TAG_CATEGORIES.map((category) => {
                    const isActive = category.id === activeCategory;
                    return (
                        <button
                            key={category.id}
                            type="button"
                            className={`author-tab${isActive ? ' active' : ''}`}
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => setActiveCategory(category.id)}
                            disabled={disabled}
                        >
                            {category.label}
                        </button>
                    );
                })}
            </div>

            {activeGroups.length === 0 ? (
                <p className="tag-group-input__empty">
                    No {TAG_CATEGORIES.find((entry) => entry.id === activeCategory)?.label ?? 'tag'} groups available.
                </p>
            ) : (
                activeGroups.map((group) => {
                    const selectedTags = assignmentMap.get(group.id) || [];
                    const availableTags = Array.isArray(group.subTags) ? group.subTags : [];

                    return (
                        <section key={group.id} className="tag-group-input__group">
                            <header className="tag-group-input__header">
                                <h4 className="tag-group-input__title">{group.label}</h4>
                            </header>
                            <TagInput
                                name={`tag-group-${group.id}`}
                                selectedTags={selectedTags}
                                availableTags={availableTags}
                                onChange={(next) => handleGroupChange(group.id, next)}
                                placeholder="Add a tag..."
                                disabled={disabled}
                                commitOnBlur
                            />
                        </section>
                    );
                })
            )}
        </div>
    );
};

export default TagGroupInput;
